import React, { createContext, useContext, useState, useEffect } from 'react';
import { getApiUrl } from '../config/api';
import { registrarTokenFcmDesdeAndroid } from '../utils/registrarTokenFcm';

const AuthContext = createContext();

const LAST_APP_ID_KEY = 'lastAppId';

const persistLastAppId = (appid) => {
  if (typeof window === 'undefined') return;
  if (appid && typeof appid === 'string') {
    try {
      localStorage.setItem(LAST_APP_ID_KEY, appid);
    } catch (storageError) {
      console.warn('No se pudo guardar el último appid:', storageError);
    }
  }
};

const tryRegisterFcm = (userData) => {
  if (!userData?.username) return;
  // No bloquea login/sesión si falla
  registrarTokenFcmDesdeAndroid(userData).catch(() => {});
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Verificar sesión al cargar la aplicación
  useEffect(() => {
    const checkSession = async () => {
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        try {
          const userData = JSON.parse(savedUser);
          
          if (!userData?.username || !userData?.appid) {
            console.log('Sesión guardada sin datos completos, limpiando');
            localStorage.removeItem('user');
            setUser(null);
            setLoading(false);
            return;
          }
          
          const verifyUrl = `${getApiUrl('AUTH', 'VERIFY')}?username=${encodeURIComponent(userData.username)}&appid=${encodeURIComponent(userData.appid)}`;

          let controller;
          let timeoutId;
          if (typeof AbortController === 'function') {
            controller = new AbortController();
            timeoutId = setTimeout(() => {
              try {
                controller.abort();
              } catch (abortError) {
                console.warn('AbortController abort error (ignorable):', abortError);
              }
            }, 10000);
          }

          const response = await fetch(verifyUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            signal: controller?.signal
          }).finally(() => {
            if (timeoutId) {
              clearTimeout(timeoutId);
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.user) {
              const normalizedUser = {
                ...data.user,
                appid: data.user.appid || userData.appid
              };
              setUser(normalizedUser);
              localStorage.setItem('user', JSON.stringify(normalizedUser));
              persistLastAppId(normalizedUser.appid);
              tryRegisterFcm(normalizedUser);
            } else {
              localStorage.removeItem('user');
              setUser(null);
            }
          } else if (response.status === 401) {
            console.log('Sesión inválida (401), limpiando localStorage');
            persistLastAppId(userData.appid);
            localStorage.removeItem('user');
            setUser(null);
          } else {
            console.log('Error verificando sesión, manteniendo sesión local:', response.status);
            setUser(userData);
            persistLastAppId(userData.appid);
            tryRegisterFcm(userData);
          }
        } catch (error) {
          console.error('Error verificando sesión:', error);
          if (error.name === 'AbortError' || error.message.includes('Failed to fetch')) {
            console.log('Error de red, manteniendo sesión local temporalmente');
            const userData = JSON.parse(savedUser);
            setUser(userData);
            persistLastAppId(userData.appid);
            tryRegisterFcm(userData);
          } else {
            const userData = JSON.parse(savedUser);
            persistLastAppId(userData?.appid);
            localStorage.removeItem('user');
            setUser(null);
          }
        }
      }
      setLoading(false);
    };

    checkSession();
  }, []);

  const login = async (username, password, appid) => {
    try {
      const apiUrl = getApiUrl('AUTH', 'LOGIN');
      console.log('🔗 Intentando conectar a:', apiUrl);
      console.log('📡 Método: POST');
      console.log('👤 Usuario:', username);
      console.log('🆔 ID de Equipo:', appid);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password, appid }),
      });
      
      console.log('📊 Respuesta del servidor:');
      console.log('  - Status:', response.status);
      console.log('  - Status Text:', response.statusText);
      console.log('  - URL:', response.url);

      const data = await response.json();

      if (response.ok && data.success) {
        const normalizedUser = {
          ...data.user,
          appid: data.user?.appid || appid
        };
        setUser(normalizedUser);
        localStorage.setItem('user', JSON.stringify(normalizedUser));
        persistLastAppId(normalizedUser.appid);
        tryRegisterFcm(normalizedUser);
        return { success: true, user: normalizedUser, session: data.session };
      } else {
        return { success: false, error: data.error || 'Error al iniciar sesión' };
      }
    } catch (error) {
      const apiUrl = getApiUrl('AUTH', 'LOGIN');
      console.error('❌ Error en login:', error);
      console.error('🔍 Detalles del error:');
      console.error('  - Tipo:', error.name);
      console.error('  - Mensaje:', error.message);
      console.error('  - URL intentada:', apiUrl);
      console.error('  - Stack:', error.stack);
      
      let errorMessage = `Error de conexión. No se puede conectar al servidor en: ${apiUrl}`;
      
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        errorMessage = `Error de conexión: No se puede conectar al servidor. URL: ${apiUrl}`;
      } else if (error.name === 'TypeError' && error.message.includes('NetworkError')) {
        errorMessage = `Error de red: No se puede conectar al servidor. URL: ${apiUrl}`;
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      // Intentar hacer logout en el servidor (opcional)
      await fetch(getApiUrl('AUTH', 'LOGOUT'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('Error en logout:', error);
      // Continuar con el logout local incluso si falla la llamada al servidor
    } finally {
      try {
        const storedUser = user || JSON.parse(localStorage.getItem('user') || 'null');
        if (storedUser?.appid) {
          persistLastAppId(storedUser.appid);
        }
      } catch (parseError) {
        console.warn('No se pudo recuperar el usuario para preservar appid durante logout:', parseError);
      }
      setUser(null);
      localStorage.removeItem('user');
    }
  };

  const setUserFrom2FA = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    persistLastAppId(userData?.appid);
    tryRegisterFcm(userData);
  };

  const value = {
    user,
    login,
    logout,
    setUserFrom2FA,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 